import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import KOTPrintLayout from "../components/KOTPrintLayout";
import {
    cancelOrderKOT,
    getKOTConsole,
    getKOTDetail,
    shiftCompleteTableKOTs,
    shiftKOTToTable,
} from "../api/orderApi";

export default function KOTManagementPage() {
    const { user } = useAuth();
    const navigate = useNavigate();

    const restaurantId = user?.restaurantId;

    const [loading, setLoading] = useState(true);
    const [tables, setTables] = useState([]);
    const [selectedTableId, setSelectedTableId] = useState(null);
    const [selectedKOTId, setSelectedKOTId] = useState(null);
    const [selectedKOTDetail, setSelectedKOTDetail] = useState(null);
    const [kotTargetTable, setKotTargetTable] = useState("");
    const [tableShiftTarget, setTableShiftTarget] = useState("");
    const [printData, setPrintData] = useState(null);
    const [actionBusy, setActionBusy] = useState(false);

    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false; // Mark as unmounted as soon as user leaves
        };
    }, []);
    const loadConsole = useCallback(async (preferredTableId = null, preferredKotId = null) => {
        if (!restaurantId || !isMounted.current) return;

        try {
            setLoading(true);
            const data = await getKOTConsole(restaurantId);

            if (!isMounted.current) return;

            setTables(Array.isArray(data) ? data : []);

            const activeTables = (Array.isArray(data) ? data : []).filter((t) => t.activeKotCount > 0 && t?.status !==  'billpending');
            console.log(activeTables);

            if (activeTables.length === 0) {
                setSelectedTableId(null);
                setSelectedKOTId(null);
                setSelectedKOTDetail(null);
                return;
            }

            const nextTable =
                activeTables.find((t) => Number(t.seating_id) === Number(preferredTableId)) || activeTables[0];

            setSelectedTableId(nextTable.seating_id);

            const nextKot =
                (nextTable.kots || []).find((k) => Number(k.kot_id) === Number(preferredKotId)) ||
                (nextTable.kots || [])[0] ||
                null;

            setSelectedKOTId(nextKot ? nextKot.kot_id : null);
        } catch (error) {
            if (isMounted.current) {
                console.error("Failed to load KOT management data:", error);
                alert("Unable to load KOT management data.");
            }
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    }, [restaurantId]);

    useEffect(() => {
        loadConsole();
    }, [loadConsole]);

    useEffect(() => {
        if (!selectedKOTId || !restaurantId) {
            setSelectedKOTDetail(null);
            return;
        }

        const loadDetail = async () => {
            try {
                const detail = await getKOTDetail(selectedKOTId, restaurantId);
                setSelectedKOTDetail(detail);
            } catch (error) {
                console.error("Failed to fetch KOT detail:", error);
                setSelectedKOTDetail(null);
            }
        };

        loadDetail();
    }, [selectedKOTId, restaurantId]);

    const activeTables = useMemo(() => tables.filter((t) => t.activeKotCount > 0 && t?.status !==  'billpending'), [tables]);

    const selectedTable = useMemo(
        () => activeTables.find((t) => Number(t.seating_id) === Number(selectedTableId)) || null,
        [activeTables, selectedTableId]
    );

    // const selectedTableKOTs = selectedTable?.kots || [];
    const selectedTableKOTs = useMemo(
        () => selectedTable?.kots || [],
        [selectedTable]
    );

    useEffect(() => {
        if (!selectedTable) {
            setSelectedKOTId(null);
            return;
        }

        const exists = selectedTableKOTs.some((k) => Number(k.kot_id) === Number(selectedKOTId));
        if (!exists) {
            setSelectedKOTId(selectedTableKOTs[0]?.kot_id || null);
        }
    }, [selectedTable, selectedTableKOTs, selectedKOTId]);

    const shiftTargetsForKOT = useMemo(() => {
        const sourceName = selectedKOTDetail?.seating_name;
        return tables.filter((t) => t.seating_name !== sourceName && t.status !== "billpending");
    }, [tables, selectedKOTDetail]);

    const shiftTargetsForTable = useMemo(() => {
        const sourceName = selectedTable?.seating_name;
        return tables.filter((t) => t.seating_name !== sourceName && t.status !== "billpending");
    }, [tables, selectedTable]);

    const handleCancelOrder = async () => {
        if (!selectedKOTDetail) return;

        const ok = window.confirm(
            `Cancel KOT #${selectedKOTDetail.kot_id} for table ${selectedKOTDetail.seating_name}?`
        );
        if (!ok) return;

        try {
            setActionBusy(true);
            await cancelOrderKOT(selectedKOTDetail.kot_id, restaurantId);
            alert("Order cancelled successfully.");
            await loadConsole(selectedTableId);
        } catch (error) {
            console.error("Cancel order failed:", error);
            alert("Failed to cancel order.");
        } finally {
            setActionBusy(false);
        }
    };

    const handleShiftKOT = async () => {
        if (!selectedKOTDetail || !kotTargetTable) {
            alert("Please choose a target table first.");
            return;
        }

        try {
            setActionBusy(true);
            await shiftKOTToTable(selectedKOTDetail.kot_id, {
                restaurantId,
                targetSeatingName: kotTargetTable,
            });
            alert("KOT shifted successfully.");
            setKotTargetTable("");
            await loadConsole(null, selectedKOTDetail.kot_id);
        } catch (error) {
            console.error("Shift KOT failed:", error);
            alert(error?.response?.data?.message || "Failed to shift KOT.");
        } finally {
            setActionBusy(false);
        }
    };

    const handleEditKOT = () => {
        if (!selectedKOTDetail) return;
        navigate(`/order/${encodeURIComponent(selectedKOTDetail.seating_name)}`, {
            state: { selectedKOTId: String(selectedKOTDetail.kot_id) },
        });
    };

    const handleShiftCompleteTable = async () => {
        if (!selectedTable || !tableShiftTarget) {
            alert("Please choose a target table first.");
            return;
        }

        const ok = window.confirm(
            `Shift all active KOTs from ${selectedTable.seating_name} to ${tableShiftTarget}?`
        );
        if (!ok) return;

        try {
            setActionBusy(true);
            await shiftCompleteTableKOTs({
                restaurantId,
                sourceSeatingName: selectedTable.seating_name,
                targetSeatingName: tableShiftTarget,
            });
            alert("Complete table shifted successfully.");
            setTableShiftTarget("");
            await loadConsole();
        } catch (error) {
            console.error("Shift complete table failed:", error);
            alert(error?.response?.data?.message || "Failed to shift complete table.");
        } finally {
            setActionBusy(false);
        }
    };

    const handleReprintKOT = () => {
        if (!selectedKOTDetail) return;

        setPrintData({
            kotId: selectedKOTDetail.kot_id,
            seatingName: selectedKOTDetail.seating_name,
            products: selectedKOTDetail.products || [],
            createdAt: selectedKOTDetail.datetime,
            createdBy: selectedKOTDetail.updated_by || user?.name || "System",
        });

        setTimeout(() => {
            window.print();
            setPrintData(null);
        }, 100);
    };

    return (
        <div className="bg-gray-100 h-full min-h-[80vh] p-4">
            <KOTPrintLayout data={printData} user={user} />

            <div className="mb-4 flex items-center justify-between">
                <div>
                    <h1 className="text-xl font-bold text-gray-900">KOT Management Console</h1>
                    <p className="text-xs text-gray-500">Manage active KOTs by table with action controls.</p>
                </div>
                <button
                    onClick={() => loadConsole(selectedTableId, selectedKOTId)}
                    className="bg-white border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-lg text-sm"
                    disabled={loading || actionBusy}
                >
                    Refresh
                </button>
            </div>

            {loading ? (
                <div className="bg-white rounded-xl border p-8 text-center text-gray-500">Loading active KOTs...</div>
            ) : activeTables.length === 0 ? (
                <div className="bg-white rounded-xl border p-8 text-center text-gray-500">
                    No active KOTs found for this restaurant.
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    <div className="lg:col-span-5 bg-white border rounded-xl p-3 max-h-[72vh] overflow-auto">
                        <h2 className="text-sm font-bold text-gray-700 mb-3">Tables and Active KOT IDs</h2>

                        <div className="space-y-3">
                            {activeTables.map((table) => {
                                const isSelectedTable = Number(table.seating_id) === Number(selectedTableId);

                                return (
                                    <div
                                        key={table.seating_id}
                                        className={`rounded-lg border p-3 ${isSelectedTable ? "border-indigo-500" : "border-gray-200 bg-white"
                                            }`}
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <button
                                                onClick={() => setSelectedTableId(table.seating_id)}
                                                className="text-left font-semibold text-gray-900"
                                            >
                                                {table.seating_name}
                                            </button>
                                            <span className="text-xs text-gray-500">{table.activeKotCount} KOT</span>
                                        </div>

                                        <div className="flex flex-wrap gap-2">
                                            {table.kots.map((kot) => {
                                                const isKotSelected = Number(kot.kot_id) === Number(selectedKOTId);

                                                return (
                                                    <button
                                                        key={kot.kot_id}
                                                        onClick={() => {
                                                            setSelectedTableId(table.seating_id);
                                                            setSelectedKOTId(kot.kot_id);
                                                        }}
                                                        className={`text-xs px-2 py-1 rounded-full border ${isKotSelected
                                                            ? "bg-indigo-600 border-indigo-600 text-white"
                                                            : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                                                            }`}
                                                    >
                                                        KOT #{kot.kot_id}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="lg:col-span-7 bg-white border rounded-xl p-4 max-h-[72vh] overflow-auto">
                        {!selectedKOTDetail ? (
                            <div className="h-full flex items-center justify-center text-gray-400">
                                Select a KOT to view details and actions.
                            </div>
                        ) : (
                            <>
                                <div className="border-b pb-3 mb-4">
                                    <h2 className="text-lg font-bold text-gray-900">
                                        KOT #{selectedKOTDetail.kot_id} - {selectedKOTDetail.seating_name}
                                    </h2>
                                    <p className="text-xs text-gray-500">
                                        Status: {selectedKOTDetail.status} | Time: {new Date(selectedKOTDetail.datetime).toLocaleString()}
                                    </p>
                                </div>

                                <div className="bg-gray-50 border rounded-lg p-3 mb-4">
                                    <h3 className="font-semibold text-gray-800 mb-2">KOT View</h3>
                                    {(selectedKOTDetail.products || []).length === 0 ? (
                                        <p className="text-sm text-gray-400">No items available.</p>
                                    ) : (
                                        <div className="space-y-2 max-h-56 overflow-auto">
                                            {(selectedKOTDetail.products || []).map((item, idx) => (
                                                <div key={`${item.productId}-${idx}`} className="flex justify-between text-sm border-b pb-1">
                                                    <span>
                                                        {item.name} <span className="text-gray-400">x {item.quantity}</span>
                                                    </span>
                                                    <span className="font-medium">₹{(Number(item.price || 0) * Number(item.quantity || 0)).toFixed(2)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                    <button
                                        onClick={handleCancelOrder}
                                        disabled={actionBusy}
                                        className="bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white py-2 rounded-lg font-semibold"
                                    >
                                        Cancel Order
                                    </button>

                                    <button
                                        onClick={handleEditKOT}
                                        disabled={actionBusy}
                                        className="bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white py-2 rounded-lg font-semibold"
                                    >
                                        Edit KOT
                                    </button>

                                    <button
                                        onClick={handleReprintKOT}
                                        disabled={actionBusy}
                                        className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white py-2 rounded-lg font-semibold"
                                    >
                                        Re-Print KOT
                                    </button>

                                    <div className="border rounded-lg p-2">
                                        <label className="text-xs text-gray-600 block mb-1">Shift KOT to another table</label>
                                        <div className="flex gap-2">
                                            <select
                                                value={kotTargetTable}
                                                onChange={(e) => setKotTargetTable(e.target.value)}
                                                className="flex-1 border rounded px-2 py-1 text-sm"
                                            >
                                                <option value="">Select table</option>
                                                {shiftTargetsForKOT.map((table) => (
                                                    <option key={`kot-target-${table.seating_id}`} value={table.seating_name}>
                                                        {table.seating_name}
                                                    </option>
                                                ))}
                                            </select>
                                            <button
                                                onClick={handleShiftKOT}
                                                disabled={actionBusy}
                                                className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-3 rounded text-sm"
                                            >
                                                Shift
                                            </button>
                                        </div>
                                    </div>

                                    <div className="border rounded-lg p-2 md:col-span-2">
                                        <label className="text-xs text-gray-600 block mb-1">Shift Complete Table (all active KOTs)</label>
                                        <div className="flex gap-2">
                                            <select
                                                value={tableShiftTarget}
                                                onChange={(e) => setTableShiftTarget(e.target.value)}
                                                className="flex-1 border rounded px-2 py-1 text-sm"
                                            >
                                                <option value="">Select target table</option>
                                                {shiftTargetsForTable.map((table) => (
                                                    <option key={`table-target-${table.seating_id}`} value={table.seating_name}>
                                                        {table.seating_name}
                                                    </option>
                                                ))}
                                            </select>
                                            <button
                                                onClick={handleShiftCompleteTable}
                                                disabled={actionBusy}
                                                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-3 rounded text-sm"
                                            >
                                                Shift Complete Table
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
